/**
 * General purpose Dropdown component
 * 
 * This is a reusable dropdown component that provides a consistent interface
 * for creating dropdowns throughout the application. It's based on the 
 * avatar-dropdown implementation but generalized for broader use.
 * 
 * Features:
 * - Configurable trigger elements (any ReactNode)
 * - Pre-defined menu items with icons and click handlers
 * - Custom content via children prop
 * - Destructive item variants
 * - Flexible alignment and sizing
 * - Consistent styling with design system tokens
 * 
 * @example
 * // Simple dropdown with items
 * <Dropdown 
 *   trigger={<Button>Actions</Button>}
 *   items={[
 *     { icon: 'edit-line', label: 'Edit', onClick: () => {} },
 *     { icon: 'delete-line', label: 'Delete', variant: 'destructive', onClick: () => {} }
 *   ]}
 * />
 * 
 * @example
 * // Dropdown with custom content
 * <Dropdown trigger={<Avatar />}>
 *   <CustomMenuItem />
 *   <Separator />
 *   <LogoutButton />
 * </Dropdown>
 */

import React, { useState } from 'react';
import { ToolbarPopoverHeader } from '~/design-system/components/Toolbar/ToolbarPopoverPanel';
import toolbarPopoverStyles from '~/design-system/components/Toolbar/ToolbarPopoverPanel.module.css';
import { 
  DropdownMenu,
  DropdownMenuContent, 
  DropdownMenuItem,
  DropdownMenuTrigger
} from "./DropdownMenu";
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { useDemoScaledPortal } from '~/design-system/components/InteractiveDemo/DemoScaledPortalContext';
import styles from './Dropdown.module.css';

export interface DropdownItemProps {
  id?: string;
  /** When true, this entry renders as a thin divider line instead of a menu item (label is ignored). */
  separator?: boolean;
  icon?: string;
  iconSvg?: string | React.ReactNode;
  label: string;
  caption?: string;
  badge?: React.ReactNode;
  tailIcon?: string;
  onClick?: () => void;
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  asChild?: boolean;
  children?: React.ReactNode;
  showCaption?: boolean;
  showBadge?: boolean;
  showTailIcon?: boolean;
}

interface DropdownProps {
  trigger: React.ReactNode;
  items?: DropdownItemProps[];
  children?: React.ReactNode;
  /** Custom content rendered ABOVE the menu items (e.g. an account balance summary). */
  headerContent?: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
  contentClassName?: string;
  /** Inline style merged onto the dropdown panel (e.g. extra padding). Wins over the base content CSS. */
  contentStyle?: React.CSSProperties;
  width?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  collisionPadding?: number;
  panelTitle?: string;
  onOpenChange?: (open: boolean) => void;
}

export function Dropdown({
  trigger,
  items = [],
  children,
  headerContent,
  align = 'end',
  className,
  contentClassName,
  contentStyle,
  width = 180,
  side,
  sideOffset,
  collisionPadding,
  panelTitle,
  onOpenChange,
}: DropdownProps) {
  const demoScaleContext = useDemoScaledPortal();
  const [panelOpen, setPanelOpen] = useState(false);
  const usesPanelHeader = Boolean(panelTitle);

  const handleOpenChange = (open: boolean) => {
    if (usesPanelHeader) {
      setPanelOpen(open);
    }
    onOpenChange?.(open);
  };

  const renderMenuItem = (item: DropdownItemProps, index: number) => {
    if (item.separator) {
      return <div key={item.id || index} className={styles.separator} role="separator" aria-orientation="horizontal" />;
    }

    if (item.disabled) {
      // Render headers as non-interactive items
      return (
        <div key={item.id || index} className={styles.dropdownMenuHeader}>
          {item.iconSvg ? (
            <div className={styles.iconContainer}>
              {typeof item.iconSvg === 'string' ? (
                <img src={item.iconSvg} alt="" style={{ width: 16, height: 16 }} />
              ) : (
                item.iconSvg
              )}
            </div>
          ) : item.icon && (
            <div className={styles.iconContainer}>
              <DeckIcon name={item.icon} size={16} />
            </div>
          )}
          <div className={styles.labelContainer}>
            <span className={styles.label}>{item.label}</span>
          </div>
        </div>
      );
    }
    
    const showCaption = item.showCaption !== false && item.caption;
    const showBadge = item.showBadge !== false && item.badge;
    const itemClassName = `${styles.dropdownMenuItem} ${
      showCaption ? styles.dropdownMenuItemStacked : ''
    } ${
      item.variant === 'destructive' ? styles.destructiveItem : ''
    }`;

    const content = (
      <>
        {item.iconSvg ? (
          <div className={styles.iconContainer}>
            {typeof item.iconSvg === 'string' ? (
              <img src={item.iconSvg} alt="" style={{ width: 16, height: 16 }} />
            ) : (
              item.iconSvg
            )}
          </div>
        ) : item.icon && (
          <div className={styles.iconContainer}>
            <DeckIcon name={item.icon} size={16} />
          </div>
        )}
        <div className={`${styles.labelContainer} ${showCaption ? styles.labelContainerStacked : ''}`}>
          <span className={styles.label}>{item.label}</span>
          {showCaption && (
            <span className={styles.caption}>{item.caption}</span>
          )}
        </div>
        {showBadge && (
          <div className={styles.badgeContainer}>
            {item.badge}
          </div>
        )}
        {item.showTailIcon !== false && item.tailIcon && (
          <div className={styles.tailIconContainer}>
            <DeckIcon name={item.tailIcon} size={16} />
          </div>
        )}
      </>
    );

    if (item.asChild && item.children) {
      return (
        <DropdownMenuItem 
          key={item.id || index}
          className={styles.childItem}
          onSelect={(e) => e.preventDefault()}
          asChild
        >
          {item.children}
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuItem
        key={item.id || index}
        className={itemClassName}
        onClick={item.onClick}
      >
        {content}
      </DropdownMenuItem>
    );
  };

  const isInScaledDemo = demoScaleContext != null;

  return (
    <DropdownMenu
      open={usesPanelHeader ? panelOpen : undefined}
      onOpenChange={handleOpenChange}
    >
      <DropdownMenuTrigger asChild className={className}>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        avoidCollisions
        className={`${styles.dropdownMenuContent} ${
          isInScaledDemo ? styles.dropdownMenuContentInDemo : ''
        } ${usesPanelHeader ? toolbarPopoverStyles.dropdownContent : ''} ${contentClassName || ''}`}
        style={{
          width: `${width}px`,
          ...(isInScaledDemo ? { zoom: demoScaleContext.scale } : {}),
          ...contentStyle,
        }}
      >
        {usesPanelHeader && panelTitle ? (
          <ToolbarPopoverHeader title={panelTitle} onClose={() => handleOpenChange(false)} />
        ) : null}
        <div
          className={`${styles.menuContainer} ${
            usesPanelHeader ? toolbarPopoverStyles.dropdownMenuBody : ''
          }`}
        >
          {headerContent}
          {items.map((item, index) => renderMenuItem(item, index))}
          {children}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Export individual components for more granular control
export { DropdownMenuItem as DropdownItem } from "./DropdownMenu";